import React from 'react';

const ProjectsPage: React.FC = () => {
  const projectCategories = [
    {
      title: 'Offshore Platform Support',
      description: 'Comprehensive support services for offshore oil and gas platforms',
      projects: ['Platform maintenance support', 'Personnel transportation', 'Supply operations', 'Emergency response'],
      icon: (
        <svg className="w-8 h-8 text-ocean-600" fill="currentColor" viewBox="0 0 20 20">
          <path d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"/>
        </svg>
      )
    },
    {
      title: 'Marine Construction',
      description: 'Specialized vessels and crew for marine construction projects',
      projects: ['Pipeline installation support', 'Subsea construction', 'Heavy lift operations', 'Installation support'],
      icon: (
        <svg className="w-8 h-8 text-ocean-600" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11.707 4.707a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
        </svg>
      )
    },
    {
      title: 'Survey & Inspection',
      description: 'Precision survey and inspection services using specialized vessels',
      projects: ['Bathymetric surveys', 'Pipeline inspections', 'Environmental monitoring', 'Geotechnical surveys'],
      icon: (
        <svg className="w-8 h-8 text-ocean-600" fill="currentColor" viewBox="0 0 20 20">
          <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
      )
    },
    {
      title: 'Emergency Response',
      description: '24/7 emergency response and crisis management services',
      projects: ['Search and rescue operations', 'Oil spill response', 'Medical evacuations', 'Emergency repairs'],
      icon: (
        <svg className="w-8 h-8 text-ocean-600" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"/>
        </svg>
      )
    }
  ];

  const featuredProjects = [
    {
      title: 'Major Platform Support Contract',
      client: 'International Oil Company',
      duration: '2023 - Ongoing',
      description: 'Providing comprehensive vessel support for offshore platform operations including crew transfer, supply runs, and emergency response.',
      scope: ['24/7 vessel availability', 'Certified crew members', 'Emergency response capability', 'Supply chain management']
    },
    {
      title: 'Pipeline Installation Support',
      client: 'Marine Construction Contractor',
      duration: '2022 - 2023',
      description: 'Specialized vessel support for subsea pipeline installation project in the Arabian Gulf.',
      scope: ['Anchor handling operations', 'Survey support', 'Personnel transportation', 'Equipment mobilization']
    },
    {
      title: 'Environmental Monitoring Program',
      client: 'Government Environmental Agency',
      duration: '2022 - Ongoing',
      description: 'Long-term environmental monitoring and data collection program using specialized survey vessels.',
      scope: ['Water quality monitoring', 'Marine life surveys', 'Data collection and analysis', 'Regular reporting']
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-blue-100 to-ocean-100">
      <div className="section">
        <h1 className="section-header">Our Projects</h1>
        
        <div className="maritime-card p-8 mb-16 text-center">
          <h2 className="text-2xl font-bold text-ocean-800 mb-4">Project Excellence</h2>
          <p className="text-lg text-gray-700 max-w-4xl mx-auto leading-relaxed">
            PetroMarine has successfully completed numerous projects across the Middle East, 
            delivering exceptional results for our clients in the offshore oil and gas industry. 
            Our project portfolio demonstrates our capability to handle complex marine operations 
            with precision and reliability.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 mb-16">
          <div className="maritime-card p-8">
            <h2 className="text-2xl font-bold text-ocean-800 mb-6">Project Statistics</h2>
            <div className="space-y-4">
              <div className="flex justify-between items-center py-3 border-b border-gray-200">
                <span className="font-medium text-gray-700">Projects Completed</span>
                <span className="text-xl font-bold text-ocean-600">150+</span>
              </div>
              <div className="flex justify-between items-center py-3 border-b border-gray-200">
                <span className="font-medium text-gray-700">Client Satisfaction Rate</span>
                <span className="text-xl font-bold text-green-600">98%</span>
              </div>
              <div className="flex justify-between items-center py-3 border-b border-gray-200">
                <span className="font-medium text-gray-700">On-Time Delivery</span>
                <span className="text-xl font-bold text-green-600">95%</span>
              </div>
              <div className="flex justify-between items-center py-3">
                <span className="font-medium text-gray-700">Safety Record</span>
                <span className="text-xl font-bold text-green-600">Zero LTI</span>
              </div>
            </div>
          </div>

          <div className="maritime-card p-8">
            <h2 className="text-2xl font-bold text-ocean-800 mb-6">Our Approach</h2>
            <ul className="space-y-4 text-gray-700">
              <li className="flex items-start">
                <span className="w-2 h-2 bg-ocean-500 rounded-full mr-3 mt-2"></span>
                <span>Detailed project planning and risk assessment</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-ocean-500 rounded-full mr-3 mt-2"></span>
                <span>Dedicated project management and coordination</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-ocean-500 rounded-full mr-3 mt-2"></span>
                <span>Real-time monitoring and progress reporting</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-ocean-500 rounded-full mr-3 mt-2"></span>
                <span>Quality assurance and safety compliance</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-ocean-500 rounded-full mr-3 mt-2"></span>
                <span>Post-project support and maintenance</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mb-16">
          <h2 className="text-2xl font-bold text-ocean-800 mb-8 text-center">Service Categories</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {projectCategories.map((category, index) => (
              <div key={index} className="maritime-card p-8">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-ocean-100 rounded-full flex items-center justify-center mr-4">
                    {category.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-ocean-800">{category.title}</h3>
                </div>
                <p className="text-gray-600 mb-6">{category.description}</p>
                <ul className="space-y-2">
                  {category.projects.map((project, projectIndex) => (
                    <li key={projectIndex} className="flex items-center text-gray-700">
                      <span className="w-1.5 h-1.5 bg-ocean-500 rounded-full mr-3"></span>
                      {project}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="mb-16">
          <h2 className="text-2xl font-bold text-ocean-800 mb-8 text-center">Featured Projects</h2>
          <div className="space-y-8">
            {featuredProjects.map((project, index) => (
              <div key={index} className="maritime-card p-8">
                <div className="grid lg:grid-cols-4 gap-8">
                  {/* Project Image */}
                  <div className="lg:col-span-1">
                    <div className="relative overflow-hidden rounded-xl">
                      <img
                        src={
                          index === 0 
                            ? 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80'
                            : index === 1
                            ? 'https://images.unsplash.com/photo-1581833971358-2c8b550f87b3?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80'
                            : 'https://images.unsplash.com/photo-1578662996442-48f60103fc96?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80'
                        }
                        alt={project.title}
                        className="w-full h-48 object-cover transition-transform duration-300 hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                    </div>
                  </div>
                  
                  {/* Project Details */}
                  <div className="lg:col-span-2">
                    <div className="flex items-start justify-between mb-4">
                      <h3 className="text-xl font-semibold text-ocean-800">{project.title}</h3>
                      <span className="bg-ocean-100 text-ocean-800 px-3 py-1 rounded-full text-sm font-medium">
                        {project.duration}
                      </span>
                    </div>
                    <p className="text-gray-600 mb-2">
                      <span className="font-medium">Client:</span> {project.client}
                    </p>
                    <p className="text-gray-700 mb-6 leading-relaxed">{project.description}</p>
                  </div>
                  
                  {/* Project Scope */}
                  <div className="lg:col-span-1">
                    <h4 className="font-semibold text-ocean-800 mb-3">Project Scope</h4>
                    <ul className="space-y-2">
                      {project.scope.map((item, itemIndex) => (
                        <li key={itemIndex} className="flex items-start text-sm text-gray-700">
                          <span className="w-1.5 h-1.5 bg-ocean-500 rounded-full mr-2 mt-2"></span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="maritime-card p-8 mb-16">
          <h2 className="text-2xl font-bold text-ocean-800 mb-6 text-center">Project Capabilities</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z"/>
                </svg>
              </div>
              <h3 className="font-semibold text-ocean-800 mb-2">Expert Team</h3>
              <p className="text-gray-600 text-sm">Experienced project managers and marine professionals</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z"/>
                </svg>
              </div>
              <h3 className="font-semibold text-ocean-800 mb-2">Modern Equipment</h3>
              <p className="text-gray-600 text-sm">State-of-the-art vessels and specialized equipment</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
              </div>
              <h3 className="font-semibold text-ocean-800 mb-2">Quality Assurance</h3>
              <p className="text-gray-600 text-sm">Rigorous quality control and safety standards</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                </svg>
              </div>
              <h3 className="font-semibold text-ocean-800 mb-2">Proven Track Record</h3>
              <p className="text-gray-600 text-sm">Successful project delivery with excellent client satisfaction</p>
            </div>
          </div>
        </div>

        <div className="maritime-card p-8 text-center">
          <h2 className="text-2xl font-bold text-ocean-800 mb-4">Start Your Project</h2>
          <p className="text-gray-700 mb-6 max-w-3xl mx-auto leading-relaxed">
            Ready to discuss your next offshore project? Our experienced team is here to provide 
            customized solutions that meet your specific requirements and exceed your expectations.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="btn-primary">
              Request Project Consultation
            </button>
            <button className="btn-secondary">
              Download Project Portfolio
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectsPage;